import { Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { Store } from "@ngrx/store";
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import { map, takeUntil, tap, withLatestFrom } from "rxjs/operators";
import { AllCoordinateReferenceSystemsRequested, selectAllCoordinateReferenceSystems } from "src/app/core/linked-tables";
import { currentUser } from "src/app/core/mad-auth/mad-auth.store";
import { MapsService } from "src/app/core/map/maps.service";
import { formControlValueChanges } from "src/app/core/v2/utils";
import { CoordinatePoint } from "src/app/core/v3/coordinate-points";
import { LandArea } from 'src/app/core/v3/land-area'
import { ParcelIdentificationFormGroup } from "./form-group";

interface Point {
  latitude: number
  longitude: number
  length: number
}

interface State {
  centerLat: number;
  centerLng: number;
  markerInfo?: {
    lat: number | null;
    lng: number | null;
  }
  points: CoordinatePoint[],
  drawingManagerInitiliazed: boolean,
  landAreas: LandArea[],
  defaultUnitMeasurementId?: number
  userUnitMeasurementId: number
  landAreaTitle?: string
}

@Component({
  selector: 'app-parcel-identification',
  templateUrl: './parcel-identification.component.html',
  styleUrls: ['./parcel-identification.component.scss'],
  providers: [ComponentStore]
})
export class ParcelIdentificationComponent implements OnInit, OnDestroy {
  private _unitOfAreaMeasurementId$ = new BehaviorSubject<number>(null)
  @Input() form!: ParcelIdentificationFormGroup

  @Input() 
  set unitOfAreaMeasurement(value: number) {
    this._unitOfAreaMeasurementId$.next(value)
  }

  @Input() isBuilding: boolean = false

  @Input()
  set centerLat(value: number | null) {
    if (!value) return;
    this.componentStore.patchState({ centerLat: value })
  }

  @Input()
  set centerLng(value: number | null) {
    if (!value) return
    this.componentStore.patchState({ centerLng: value })
  }

  @Input()
  set marker(value: { lat: number | null, lng: number | null } | null) {
    if (!value) return;
    this.componentStore.patchState({ markerInfo: value })
  }

  // Workaround for map
  private _points: CoordinatePoint[] = []
  @Input()
  set coordinatePoints(value: CoordinatePoint[]|null) {
    if (!value) return;
    this.componentStore.patchState({ points: value })
    this._points = value
    if (value && value.length > 0) {
      this.createPolygon(value)
    }
  }

  @Input()
  set landAreas(value: LandArea[] | null) {
    if (!value) return;
    this.componentStore.patchState({ landAreas: value })
  }

  @Input()
  set defaultUnitMeasurementId(value: number) {
    this.componentStore.patchState({ defaultUnitMeasurementId: value })
  }

  @Input()
  set landAreaLowLevelTitle(value: string) {
    if (!value) return
    this.componentStore.patchState({ landAreaTitle: value })
  }

  @Output() coordinatePointsChange = new EventEmitter<CoordinatePoint[]>()
  @Output() landAreasChange = new EventEmitter<LandArea[]>()

  private map: google.maps.Map = null
  private shape: google.maps.Polygon = null
  private mapClickListener: google.maps.MapsEventListener
  private drawingManager: google.maps.drawing.DrawingManager
  private shapeDisabled: boolean = false
  imperial_area = 10.7639;
  imperial_length = 3.28084;

  unitOfAreaMeasurementId$ = this._unitOfAreaMeasurementId$.asObservable()
  private _defaultUnitMeausurementId$ = this.componentStore.select(state => state.defaultUnitMeasurementId)
  private _userUnitMeasurementId$ = this.componentStore.select(state => state.userUnitMeasurementId)
  landAreaLowLevelTitle$ = this.componentStore.select(state => state.landAreaTitle)
  hasLowLevelLandAreaTitle$ = this.componentStore.select(
    this.landAreaLowLevelTitle$,
    (title) => {
      return {
        title: title
      }
    }
  )
  staticUnit$ = this.componentStore.select(
    this._defaultUnitMeausurementId$,
    this._userUnitMeasurementId$,
    (_default, _user) => {
      if (_default) return _default
      return _user
    }
  )
  staticUnitName$ = this.componentStore.select(
    this.staticUnit$,
    (unit) => {
      if (unit == 2) return 'sqm';
      return 'sqft'
    }
  )
  staticUnitLengthName$ = this.componentStore.select(
    this.staticUnit$,
    (unit) => {
      if (unit == 2) return 'M'
      return 'Ft'
    }
  )

  private centerLat$ = this.componentStore.select(state => state.centerLat)
  private centerLng$ = this.componentStore.select(state => state.centerLng)
  private points$ = this.componentStore.select(state => state.points)
  private markerInfo$ = this.componentStore.select(state => state.markerInfo)
  private drawingManagerInitiliazed$ = this.componentStore.select(state => state.drawingManagerInitiliazed)
  private onDestroy$ = new Subject<void>()
  marker$ = this.componentStore.select(
    this.markerInfo$,
    (marker) => {
      if (!marker) return undefined
      if (marker.lat === undefined) return undefined
      if (marker.lng === undefined) return undefined
      return marker
    }
  )

  coordinateReferenceSystems$ = this.ngStore.select(
    selectAllCoordinateReferenceSystems
  )

  landAreas$ = this.componentStore.select(state => state.landAreas)
  centerLatitude$ = combineLatest([
    this.centerLat$,
    this.mapService.latitude$
  ]).pipe(map(([incoming, def]) => coordinatePicker(incoming, def)))
  centerLongitude$ = combineLatest([
    this.centerLng$,
    this.mapService.longitude$
  ]).pipe(map(([incoming, def]) => coordinatePicker(incoming, def)))
  dataSource$: Observable<Point[]> = this.componentStore.select(
    this.points$,
    (points) => {
      const items = mapCoordinateToDataSource(points)
      return items
    }
  )
  area$ = this.componentStore.select(
    this.points$,
    this.staticUnit$,
    (points, staticUnit) => {
      if (points.filter(p => p.latitude == null || p.longitude == null).length > 0) {
        return null;
      } else {
        const area = google.maps.geometry.spherical.computeArea(points.map(p => (new google.maps.LatLng(p.latitude, p.longitude))));
        return staticUnit == 1 ? area * this.imperial_area : area
      }
    }
  )

  constructor(
    private componentStore: ComponentStore<State>,
    private mapService: MapsService,
    private ngStore: Store,
    private ngZone: NgZone
  ) {
    this.componentStore.setState({
      centerLat: null,
      centerLng: null,
      points: [],
      drawingManagerInitiliazed: false,
      landAreas: [],
      userUnitMeasurementId: 2
    })
  }

  ngOnInit(): void {
    this.ngStore.dispatch(new AllCoordinateReferenceSystemsRequested());

    combineLatest([
      formControlValueChanges(this.form.controls.boundary_type),
      this.drawingManagerInitiliazed$
    ]).pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(([value, initialized]) => {
      if (!initialized) return;
      if (value !== 2) {
        this.disableDrawing()
        if (this.shape) {
          this.shape.setMap(null)
        }
        this.coordinatePointsChange.emit([])
      } else {
        this.enableDrawing()
      }
    })

    this.ngStore.select(currentUser).pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(user => {
      if (!user) {
        this.componentStore.patchState({ userUnitMeasurementId: 1 })
        return
      }
      if (!user.settings) {
        this.componentStore.patchState({ userUnitMeasurementId: 1 })
        return
      }
      this.componentStore.patchState({
        userUnitMeasurementId:
          user.settings.unit_of_measurement_id == 2
            ? 1
            : 2
      })
    })
  }

  ngOnDestroy(): void {
    if (this.mapClickListener) {
      this.mapClickListener.remove()
    }
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  onMapReady(map: google.maps.Map) {
    map.controls[google.maps.ControlPosition.TOP_CENTER].push(document.getElementById('DeleteShape'));
    this.map = map
    this.initializeDrawingManager(map)
    if (this._points.length > 0) {
      this.createPolygon(this._points)
    }
    this.mapClickListener = this.map.addListener('rightclick', (e: google.maps.MouseEvent) => {
      this.ngZone.run(() => {
        this.mapRightClick(e)
      })
    })
  }

  onDeleteShape() {
    if (this.shape) {
      this.shape.setMap(null)
    }
    this.coordinatePointsChange.emit([])
    this.enableDrawing()
  }

  onAddPoint = this.componentStore.effect<void>(trigger$ =>
    trigger$.pipe(
      withLatestFrom(this.componentStore.state$),
      tap(([_, state]: [void, State]) => {
        const points = [...state.points, { latitude: null, longitude: null }]
        this.coordinatePointsChange.emit(points)
      })
    )
  )

  onFocusOut(event: FocusEvent, type: 'lat' | 'lng', item: Point) {
    const el = event.target as HTMLInputElement;
    const val = Number(el.value);
    this.focusOut({ value: val, type, item })
  }

  onLandAreasChange(items: LandArea[]) {
    this.landAreasChange.emit(items)
  }

  private focusOut = this.componentStore.effect<{
    value: number,
    type: 'lat' | 'lng',
    item: Point
  }>(payload$ =>
    payload$.pipe(
      withLatestFrom(this.componentStore.state$),
      tap(([payload, state]: [{ value: number, type: 'lat' | 'lng', item: Point }, State]) => {
        const { item, type, value } = payload
        const index = state.points.findIndex(p => p.latitude == item.latitude && p.longitude == item.longitude);
        if (index == -1) return;
        let newItem = { ...state.points[index] };
        switch (type) {
          case 'lat':
            newItem.latitude = value;
            break;
          case 'lng':
            newItem.longitude = value;
            break;
        }
        const points = [...state.points]
        points.splice(index, 1, newItem)
        this.coordinatePointsChange.emit(points)
      })
    )
  )

  private initializeDrawingManager(map: google.maps.Map) {
    const options: google.maps.drawing.DrawingManagerOptions = {
      drawingMode: google.maps.drawing.OverlayType.POLYGON,
      drawingControl: true,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        drawingModes: [
          google.maps.drawing.OverlayType.POLYGON
        ]
      },
      polygonOptions: {
        draggable: false,
        editable: true,
        clickable: false,
        fillColor: '#f74a55',
        strokeColor: '#f74a55'
      }
    }

    const drawingManager = new google.maps.drawing.DrawingManager(options);
    drawingManager.setMap(map);
    this.drawingManager = drawingManager
    this.componentStore.patchState({ drawingManagerInitiliazed: true })

    google.maps.event.addListener(drawingManager, 'overlaycomplete', (event) => {
      if (event.type === google.maps.drawing.OverlayType.POLYGON) {
        const points = this.getPointsFromPath(event.overlay.getPath());
        this.createPolygon(points);
        event.overlay.setMap(null);
      }
      if (event.type !== google.maps.drawing.OverlayType.MARKER) {
        this.disableDrawing();
      }
    })
  }

  private disableDrawing() {
    this.drawingManager.setDrawingMode(null);
    this.drawingManager.setOptions({
      drawingControl: false,
      polygonOptions: {
        draggable: false,
        editable: true,
        clickable: false,
        fillColor: '#f74a55',
        strokeColor: '#f74a55'
      }
    })
  }

  private enableDrawing() {
    this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON)
    this.drawingManager.setOptions({
      drawingControl: true,
      polygonOptions: {
        draggable: false,
        editable: true,
        clickable: false,
        fillColor: '#f74a55',
        strokeColor: '#f74a55'
      }
    })
  }

  private createPolygon(points: CoordinatePoint[]) {
    if (this.shape) {
      this.shape.setMap(null)
    }
    const filtered = points
      .filter(p => p.latitude !== null && p.longitude !== null)
      .map(p => ({ lat: p.latitude, lng: p.longitude }))
    this.shape = new google.maps.Polygon({
      paths: filtered,
      editable: !this.shapeDisabled,
      fillColor: '#f74a55',
      strokeColor: '#f74a55'
    })
    this.shape.addListener('rightclick', (e: google.maps.MouseEvent) => {
      this.mapRightClick(e)
    })
    this.shape.setMap(this.map)

    const self = this;
    const paths = this.shape.getPath()
    for (let p = 0; p < paths.getLength(); p++) {
      google.maps.event.addListener(paths.getAt(p), 'set_at', () => {
        self.updatePaths(this.shape.getPath());
      });
      google.maps.event.addListener(paths.getAt(p), 'insert_at', () => {
        self.updatePaths(this.shape.getPath());
      });
      google.maps.event.addListener(paths.getAt(p), 'remove_at', () => {
        self.updatePaths(this.shape.getPath());
      })
    }
    this.updatePoints(points)
  }

  private mapRightClick(event: google.maps.MouseEvent) { }

  private updatePaths(path: google.maps.MVCArray<google.maps.LatLng>) {
    const points = this.getPointsFromPath(path)
    this.updatePoints(points)
  }

  private updatePoints(points: CoordinatePoint[]) {
    this.coordinatePointsChange.emit(points)
  }

  private getPointsFromPath(path: google.maps.MVCArray<google.maps.LatLng>): CoordinatePoint[] {
    const points: CoordinatePoint[] = []
    const len = path.getLength();
    for (let i = 0; i < len; i++) {
      const value = path.getAt(i).toJSON()
      points.push({ latitude: value.lat, longitude: value.lng });
    }
    return points;
  }
}

function coordinatePicker(incoming: number, def: number) {
  if (incoming == null) return def
  return incoming
}

function mapCoordinateToDataSource(points: CoordinatePoint[]): Point[] {
  const tmp: Point[] = points.map(p => ({
    latitude: p.latitude,
    longitude: p.longitude,
    length: 0
  }));
  for (let i = 1; i < points.length; i++) {
    if (i == points.length - 1) {
      tmp[0].length = calculateLengthBetweenPoints(points[0], points[points.length - 1]);
      tmp[i].length = calculateLengthBetweenPoints(points[i], points[i - 1]);
    } else {
      tmp[i].length = calculateLengthBetweenPoints(points[i], points[i - 1]);
    }
  }
  return tmp
}

function calculateLengthBetweenPoints(p1: CoordinatePoint, p2: CoordinatePoint) {
  const pathP2P = [];
  const latlng1 = new google.maps.LatLng(p1.latitude, p1.longitude);
  const latlng2 = new google.maps.LatLng(p2.latitude, p2.longitude);
  pathP2P.push(latlng1);
  pathP2P.push(latlng2);

  return google.maps.geometry.spherical.computeLength(pathP2P);
}